
import MeasureNecessaryTimeRunningButton from "./MeasureNecessaryTimeRunningButton.vue";
import DiagramTrainTypeNecessaryTimeTable from "./DiagramTrainTypeNecessaryTimeTable.vue";
import Timer from "@/logics/Timer";
import { NecessaryTime, NecessaryTimeMap, TrainType } from "@/types";
import { generateInitialNecessaryTime } from "@/logics/diagram";
import dayjs, { Dayjs } from "dayjs";
import { computed, defineComponent, PropType, ref, watch } from "vue";
import { MULTIPLYERS, FAST_FORWARDS } from "@/common/const";
import { useStore } from "@/store";
import rfdc from "rfdc";
const clone = rfdc();

export default defineComponent({
  components: {
    MeasureNecessaryTimeRunningButton,
    DiagramTrainTypeNecessaryTimeTable,
  },
  props: {
    visible: { type: Boolean, required: true },
    trainType: { type: Object as PropType<TrainType>, required: true },
  },
  emits: ["update:visible", "update:trainType"],
  setup(props, context) {
    const localVisible = computed({
      get: () => props.visible,
      set: (value) => {
        context.emit("update:visible", value);
      },
    });

    const store = useStore();
    const stationList = computed(() => store.state.stationList);
    const getStation = store.getters.getStation;
    const __isRecordStation = (id: number) => {
      return stationList.value.stations.some(
        (v) => v.shouldRecordTime && v.id === id
      );
    };

    // 時計系
    const selectedMultiplyer = ref(120);
    const selectedFastForward = ref(1);
    const timer = ref(
      new Timer(selectedMultiplyer.value * selectedFastForward.value)
    );
    watch([selectedMultiplyer, selectedFastForward], () => {
      timer.value = new Timer(
        selectedMultiplyer.value * selectedFastForward.value
      );
    });
    const formattedTime = computed(() => {
      return timer.value.now.format("HH:mm");
    });

    const isRunning = ref(false);
    const isStarted = ref(false);

    // 記録系
    let temporaryStartTime: Dayjs;
    const recordedRap = ref<{
      a: NecessaryTimeMap;
      b: NecessaryTimeMap;
    }>({
      a: generateInitialNecessaryTime(stationList.value, "A"),
      b: generateInitialNecessaryTime(stationList.value, "B"),
    });
    const __initializeRecordedRap = () => {
      recordedRap.value = {
        a: generateInitialNecessaryTime(stationList.value, "A"),
        b: generateInitialNecessaryTime(stationList.value, "B"),
      };
    };

    const initialNowStationInfo: {
      sectionFrom: number;
      sectionTo: number;
      nextStation: number;
      forwards: "A" | "B";
      finish: boolean;
    } = {
      sectionFrom: 1,
      sectionTo:
        stationList.value.stations.find((v) => v.shouldRecordTime && v.id > 1)
          ?.id || 0,
      nextStation: 1,
      forwards: "A",
      finish: false,
    };
    const nowStationInfo = ref(clone(initialNowStationInfo));

    const __record = () => {
      const key = `${nowStationInfo.value.sectionFrom}-${nowStationInfo.value.sectionTo}`;
      const rec: NecessaryTime = {
        id: key,
        from: nowStationInfo.value.sectionFrom,
        to: nowStationInfo.value.sectionTo,
        necessaryTime: dayjs(timer.value.now).diff(
          temporaryStartTime,
          "minute"
        ),
      };
      if (nowStationInfo.value.forwards === "A") {
        recordedRap.value.a.set(key, rec);
      } else {
        recordedRap.value.b.set(key, rec);
      }
    };
    const __bumpSection = () => {
      nowStationInfo.value.sectionFrom = nowStationInfo.value.sectionTo;
      if (
        nowStationInfo.value.sectionTo === stationList.value.endingStationId
      ) {
        // 折返し
        nowStationInfo.value.forwards = "B";
      }
      if (nowStationInfo.value.forwards === "A") {
        nowStationInfo.value.sectionTo =
          stationList.value.stations.find(
            (v) => v.shouldRecordTime && v.id > nowStationInfo.value.sectionFrom
          )?.id || 0;
      } else {
        nowStationInfo.value.sectionTo =
          [...stationList.value.stations]
            .reverse()
            .find(
              (v) =>
                v.shouldRecordTime && v.id < nowStationInfo.value.sectionFrom
            )?.id || 0;
      }
    };
    const __bumpNextStation = () => {
      if (nowStationInfo.value.forwards === "A") {
        nowStationInfo.value.nextStation++;
      } else {
        nowStationInfo.value.nextStation--;
      }
    };
    const onClickRunning = () => {
      isRunning.value = !isRunning.value;
      // 初回処理
      if (!isStarted.value) {
        isStarted.value = true;
        timer.value.start();
        temporaryStartTime = timer.value.now;
        __bumpNextStation();
      } else {
        if (isRunning.value) {
          // 走り始めたら
          if (__isRecordStation(nowStationInfo.value.nextStation)) {
            __bumpSection();
            temporaryStartTime = timer.value.now;
          }
          __bumpNextStation();
        } else {
          // 止まったら
          if (__isRecordStation(nowStationInfo.value.nextStation)) {
            __record();
            if (nowStationInfo.value.nextStation === 1) {
              allStop();
              nowStationInfo.value.finish = true;
            }
          }
        }
      }
    };
    const onClickPassing = () => {
      if (__isRecordStation(nowStationInfo.value.nextStation)) {
        __record();
        __bumpSection();
        temporaryStartTime = timer.value.now;
      }
      __bumpNextStation();
    };
    const allStop = () => {
      isRunning.value = false;
      timer.value.stop();
    };
    const reset = () => {
      allStop();
      isStarted.value = false;
      __initializeRecordedRap();
      timer.value.reset();
      nowStationInfo.value = clone(initialNowStationInfo);
    };
    const submit = () => {
      const newTrainType = clone(props.trainType);
      newTrainType.necessaryTimesA = clone(recordedRap.value.a);
      newTrainType.necessaryTimesB = clone(recordedRap.value.b);
      context.emit("update:trainType", newTrainType);
      localVisible.value = false;
    };
    return {
      localVisible,
      stationList,
      getStation,
      MULTIPLYERS,
      FAST_FORWARDS,
      selectedMultiplyer,
      selectedFastForward,
      recordedRap,
      timer,
      formattedTime,
      onClickRunning,
      onClickPassing,
      allStop,
      reset,
      MeasureNecessaryTimeRunningButton,
      isStarted,
      isRunning,
      nowStationInfo,
      submit,
    };
  },
});
